import { Avatar, Message } from '@chatscope/chat-ui-kit-react';
import { ButtonUnstyled } from '@lifeomic/chroma-react/components/ButtonUnstyled';
import { ExpansionPanel } from '@lifeomic/chroma-react/components/ExpansionPanel';
import { IconButton } from '@lifeomic/chroma-react/components/IconButton';
import { makeStyles, Theme } from '@lifeomic/chroma-react/styles';
import { alpha } from '@mui/material/styles';
import {
  Code,
  Copy,
  FileText,
  MessageThumbsUp,
  ThumbsDown,
  ThumbsUp,
} from '@lifeomic/chromicons';
import {
  footnoteDefinitionRegexp,
  footnoteRegexp,
} from '@lifeomic/phc-web-toolkit/src/components/SubjectViewer/Modules/ExecutiveSummary/utils';
import { MermaidDiagram } from '@lifeomic/phc-web-toolkit/src/components/MermaidDiagram';
import { useNotify } from '@lifeomic/phc-web-toolkit/src/hooks';
import _get from 'lodash/get';
import _isEmpty from 'lodash/isEmpty';
import _isPlainObject from 'lodash/isPlainObject';
import React, { useMemo, useState } from 'react';
import ReactDOM from 'react-dom';
import { defineMessages, useIntl } from 'react-intl';
import ReactMarkdown from 'react-markdown';
import remarkGfm from 'remark-gfm';
import {
  PostDetails_ActivePost_Fragment,
  PostDetailsFragment,
} from '../__generated__/api.graphql';
import {
  CLINICIANS_AGENT_USERNAME,
  MEMBERS_AGENT_USERNAME,
} from '../constants';
import AgentIcon from '../images/ai-bot.svg';
import { CitationClass } from '../types';
import { getCitationClass } from './utils/getCitationClass';

// credit to https://stackoverflow.com/questions/23934656/how-can-i-copy-rich-text-contents-to-the-clipboard-with-javascript
function copyRichTextToClipboard(str: string) {
  function listener(e: ClipboardEvent) {
    e.clipboardData?.setData('text/html', str);
    e.clipboardData?.setData('text/plain', str);
    e.preventDefault();
  }
  document.addEventListener('copy', listener);
  document.execCommand('copy');
  document.removeEventListener('copy', listener);
}

function copyMarkdownToClipboard(str: string) {
  navigator.clipboard.writeText(str);
}

const useStyles = makeStyles((theme: Theme) => ({
  messageActionsPosition: {
    position: 'absolute',
    right: theme.spacing(9),
    width: theme.spacing(3),
    height: theme.spacing(3),
    paddingTop: theme.spacing(1),
  },
  messageActionsContainer: {
    display: 'flex',
    flexDirection: 'column',
    gap: theme.spacing(1),
    margin: theme.spacing(1),
  },
  messageAction: {
    padding: 0,
    margin: 0,
  },
  bottomFeedbackFooter: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    width: '100%',
    gap: theme.spacing(1),
  },
  bottomFeedbackContainer: {
    display: 'flex',
    alignItems: 'center',
    gap: theme.spacing(0.5),
  },
  bottomFeedbackButton: {
    padding: theme.spacing(0.25),
    color: theme.palette.text.secondary,
  },
  cite: {
    color: theme.palette.primary.main,
    fontSize: theme.pxToRem(13),
    lineHeight: theme.pxToRem(8),
    '&:hover, &:focus': {
      textDecoration: 'underline',
    },
  },
  citeUnresolved: {
    color: theme.palette.text.secondary,
    cursor: 'help',
    fontSize: theme.pxToRem(13),
    lineHeight: theme.pxToRem(8),
  },
  markdownTableScroller: {
    maxWidth: '100%',
    overflowX: 'auto',
    margin: theme.spacing(1, 0),
  },
  markdownTable: {
    borderCollapse: 'collapse',
    minWidth: theme.pxToRem(360),
    width: '100%',
    fontSize: theme.pxToRem(14),
    lineHeight: 1.5,
  },
  markdownTableHeaderCell: {
    backgroundColor: theme.palette.action.hover,
    border: `1px solid ${theme.palette.divider}`,
    fontWeight: theme.typography.fontWeightBold,
    padding: theme.spacing(0.75, 1),
    textAlign: 'left',
    verticalAlign: 'top',
  },
  markdownTableCell: {
    border: `1px solid ${theme.palette.divider}`,
    padding: theme.spacing(0.75, 1),
    textAlign: 'left',
    verticalAlign: 'top',
  },
  // Transient flash applied when a message is deep-linked from a Zori Pulse
  // citation, so the clinician can spot the cited message after the chat
  // scrolls it into view.
  highlighted: {
    animation: '$citationHighlight 2.4s ease-out',
    borderRadius: theme.spacing(1),
  },
  '@keyframes citationHighlight': {
    '0%': {
      backgroundColor: alpha(theme.palette.primary.main, 0.28),
    },
    '70%': {
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
    },
    '100%': {
      backgroundColor: 'transparent',
    },
  },
  '@media (prefers-reduced-motion: reduce)': {
    highlighted: {
      animation: 'none',
      backgroundColor: alpha(theme.palette.primary.main, 0.18),
    },
  },
}));

const messages = defineMessages({
  deletedMessage: {
    id: 'agentChat.chat.deletedMessage',
    defaultMessage: '<DELETED>',
  },
  leaveFeedback: {
    id: 'agentChat.chat.leaveFeedback',
    defaultMessage: 'Leave feedback on this response',
  },
  thumbsUpResponse: {
    id: 'agentChat.chat.thumbsUpResponse',
    defaultMessage: 'Good response',
  },
  thumbsDownResponse: {
    id: 'agentChat.chat.thumbsDownResponse',
    defaultMessage: 'Bad response',
  },
  copyMessage: {
    id: 'agentChat.chat.copyMessage',
    defaultMessage: 'Copy message to clipboard',
  },
  copyMarkdown: {
    id: 'agentChat.chat.copyMarkdown',
    defaultMessage: 'Copy message markdown to clipboard',
  },
  copySuccess: {
    id: 'agentChat.chat.copySuccess',
    defaultMessage: 'Message copied to clipboard',
  },
  exportToZoriReport: {
    id: 'agentChat.chat.exportToZoriReport',
    defaultMessage: 'Export to Zori Report',
  },
  unknown: {
    id: 'agentChat.chat.unknown',
    defaultMessage: 'Unknown',
  },
  unknownAuthor: {
    id: 'agentChat.chat.unknownAuthor',
    defaultMessage: 'Author Unknown',
  },
  unknownMessage: {
    id: 'agentChat.chat.unknownMessage',
    defaultMessage: 'Message Unknown',
  },
});

const parseFootnoteRef = (
  href: string | undefined,
  ariaDescribedBy: string | undefined,
): [isFootnote: boolean, refId: string | undefined] => {
  // limit the parsing to footnotes; other links can exist in agent posts
  if (ariaDescribedBy === 'footnote-label') {
    // grab the reference id; expected format is '#user-content-fn-o56'
    const hrefParts = href?.split('-');
    const refId = hrefParts ? hrefParts[hrefParts.length - 1] : undefined;
    return [true, refId];
  } else {
    return [false, undefined];
  }
};

export type ChatMessageProps = {
  post: PostDetailsFragment;
  onMouseOver: () => void;
  isHovered: boolean;
  onClickFeedback: () => void;
  /**
   * Submit positive feedback directly from the bottom thumbs-up control.
   * When omitted, the bottom feedback controls are not rendered.
   */
  onThumbsUp?: () => void;
  /**
   * Open the feedback flow for negative feedback from the bottom thumbs-down
   * control. When omitted, the bottom feedback controls are not rendered.
   */
  onThumbsDown?: () => void;
  onClickCitation: (reference: string) => void;
  onExportToZoriReport?: (
    post: PostDetails_ActivePost_Fragment,
    message: string,
  ) => void;
  isExportingToZoriReport?: boolean;
  currentUserId: string;
  feedbackAgentUsername: string;
  isHighlighted?: boolean;
  /** True while this post's content is still streaming in from the agent. */
  isStreaming?: boolean;
};

const stripFootnotesFromMessage = (message: string) =>
  message
    .replace(footnoteDefinitionRegexp, '')
    .replace(footnoteRegexp, '')
    .trim();

const isMermaidCodeClassName = (className: string | undefined): boolean =>
  /(?:^|\s)language-mermaid(?:\s|$)/.test(className ?? '');

export const ChatMessage: React.FC<ChatMessageProps> = ({
  post,
  onMouseOver,
  isHovered,
  onClickFeedback,
  onThumbsUp,
  onThumbsDown,
  onClickCitation,
  onExportToZoriReport,
  isExportingToZoriReport = false,
  currentUserId,
  feedbackAgentUsername,
  isHighlighted = false,
  isStreaming = false,
}) => {
  const classes = useStyles({});
  const { formatMessage } = useIntl();
  const displayTime = new Date(post.createdAt).toLocaleString([], {
    dateStyle: 'short',
    timeStyle: 'short',
  });
  const [notify] = useNotify();

  let maybePost =
    post.__typename == 'ActivePost'
      ? post?.message
      : formatMessage(messages.deletedMessage);
  const cleanedPost = useMemo(
    () => (maybePost ? stripFootnotesFromMessage(maybePost) : undefined),
    [maybePost],
  );

  const handleCopy = () => {
    if (!cleanedPost) return;
    const host = document.createElement('div');
    ReactDOM.render(
      [
        <ReactMarkdown remarkPlugins={[remarkGfm]} key="rendered-post">
          {cleanedPost}
        </ReactMarkdown>,
      ] as any, // I could not get the types to work here, but it functions
      host,
    );
    copyRichTextToClipboard(host.innerHTML);
    ReactDOM.unmountComponentAtNode(host);
    notify(formatMessage(messages.copySuccess), 'info', 2000);
  };
  const handleCopyMarkdown = () => {
    copyMarkdownToClipboard(cleanedPost || '');
    notify(formatMessage(messages.copySuccess), 'info', 2000);
  };
  const isPostCopyable = !!cleanedPost;
  if (!maybePost) maybePost = formatMessage(messages.unknownMessage);

  let maybeAuthor =
    post.__typename == 'ActivePost'
      ? post?.author?.profile.displayName
      : formatMessage(messages.unknownAuthor);
  if (!maybeAuthor) maybeAuthor = formatMessage(messages.unknownAuthor);

  const authorUserId =
    post.__typename == 'ActivePost'
      ? post?.author?.userId
      : formatMessage(messages.unknown);

  const metadataString =
    post.__typename == 'ActivePost' ? post.metadata : undefined;
  const metadata = useMemo(() => {
    if (!metadataString) return {};
    try {
      const parsed = JSON.parse(metadataString);
      return _isPlainObject(parsed) ? parsed : {};
    } catch {
      return {};
    }
  }, [metadataString]);

  const isAgentMessage = authorUserId !== currentUserId;
  const isZoriAgentMessage =
    authorUserId === CLINICIANS_AGENT_USERNAME ||
    authorUserId === MEMBERS_AGENT_USERNAME;

  const metadataType =
    typeof metadata.type === 'string' ? metadata.type : undefined;

  const markdownComponents = useMemo(
    () => ({
      a({ children, 'aria-describedby': ariaDescribedBy, ...props }: any) {
        const [isFootnote, refId] = parseFootnoteRef(
          props.href,
          ariaDescribedBy,
        );
        if (isFootnote) {
          const reference =
            refId && !_isEmpty(metadata) ? _get(metadata, refId) : undefined;

          const citationClass: CitationClass =
            typeof reference === 'string' ? getCitationClass(reference) : {};
          if (citationClass.citationType) {
            return (
              <ButtonUnstyled
                title={citationClass.title}
                onClick={() => {
                  if (isFootnote && typeof reference === 'string') {
                    onClickCitation(reference);
                  }
                }}
                className={classes.cite}
              >
                [{children}]
              </ButtonUnstyled>
            );
          }
          if (typeof reference === 'string' && reference.trim() !== '') {
            return (
              <span className={classes.citeUnresolved} title={reference}>
                [{children}]
              </span>
            );
          }
          return null;
        }
        return <a {...props}>{children}</a>;
      },
      section({ children, ...props }: any) {
        if (props.className === 'footnotes') {
          return null;
        }
        return <section {...props}>{children}</section>;
      },
      table({ children, className, ...props }: any) {
        return (
          <div className={classes.markdownTableScroller}>
            <table
              {...props}
              className={[classes.markdownTable, className]
                .filter(Boolean)
                .join(' ')}
            >
              {children}
            </table>
          </div>
        );
      },
      th({ children, className, ...props }: any) {
        return (
          <th
            {...props}
            className={[classes.markdownTableHeaderCell, className]
              .filter(Boolean)
              .join(' ')}
          >
            {children}
          </th>
        );
      },
      td({ children, className, ...props }: any) {
        return (
          <td
            {...props}
            className={[classes.markdownTableCell, className]
              .filter(Boolean)
              .join(' ')}
          >
            {children}
          </td>
        );
      },
      // Render ```mermaid fences as diagrams. The diagram replaces the <code>
      // child; the <pre> override below drops its wrapper so we don't end up
      // with a diagram nested inside monospace/pre styling.
      code({ className, children, ...props }: any) {
        if (isMermaidCodeClassName(className)) {
          const chart = String(children ?? '').replace(/\n$/, '');
          return <MermaidDiagram chart={chart} isStreaming={isStreaming} />;
        }
        return (
          <code className={className} {...props}>
            {children}
          </code>
        );
      },
      pre({ children, ...props }: any) {
        const child = Array.isArray(children) ? children[0] : children;
        if (
          React.isValidElement(child) &&
          isMermaidCodeClassName((child.props as any)?.className)
        ) {
          return <>{children}</>;
        }
        return <pre {...props}>{children}</pre>;
      },
    }),
    [
      metadata,
      classes.cite,
      classes.citeUnresolved,
      classes.markdownTable,
      classes.markdownTableCell,
      classes.markdownTableHeaderCell,
      classes.markdownTableScroller,
      onClickCitation,
      isStreaming,
    ],
  );

  const messageContent = (
    <ReactMarkdown remarkPlugins={[remarkGfm]} components={markdownComponents}>
      {maybePost}
    </ReactMarkdown>
  );
  const activePost = post.__typename === 'ActivePost' ? post : undefined;
  const exportMessage = activePost?.message?.trim()
    ? activePost.message
    : undefined;
  const canExportToZoriReport =
    !!activePost &&
    !!exportMessage &&
    isZoriAgentMessage &&
    !!onExportToZoriReport;

  // Persistent thumbs up/down at the bottom of the bubble. Shown for the same
  // agent responses that expose the side feedback button, but always visible
  // (not hover-gated) and hidden while the response is still streaming in.
  const showBottomFeedback =
    isAgentMessage &&
    authorUserId === feedbackAgentUsername &&
    !isStreaming &&
    !!onThumbsUp &&
    !!onThumbsDown;

  return (
    <Message
      id={post.id}
      className={isHighlighted ? classes.highlighted : undefined}
      data-highlighted={isHighlighted ? 'true' : undefined}
      avatarPosition="top-left"
      onMouseOver={onMouseOver}
      model={{
        message: maybePost,
        sentTime: post.createdAt,
        sender: maybeAuthor,
        direction: authorUserId === currentUserId ? 'outgoing' : 'incoming',
        position: 'single',
      }}
    >
      {isAgentMessage && isHovered && (
        <Message.Header
          sender={maybeAuthor}
          className={classes.messageActionsPosition}
        >
          <div className={classes.messageActionsContainer}>
            {authorUserId === feedbackAgentUsername && (
              <IconButton
                aria-label={formatMessage(messages.leaveFeedback)}
                icon={MessageThumbsUp}
                onClick={onClickFeedback}
                className={classes.messageAction}
              />
            )}
            {isPostCopyable && (
              <IconButton
                title={formatMessage(messages.copyMessage)}
                aria-label={formatMessage(messages.copyMessage)}
                icon={Copy}
                onClick={handleCopy}
                className={classes.messageAction}
              />
            )}
            {canExportToZoriReport && (
              <IconButton
                title={formatMessage(messages.exportToZoriReport)}
                aria-label={formatMessage(messages.exportToZoriReport)}
                icon={FileText}
                onClick={() =>
                  activePost &&
                  onExportToZoriReport(activePost, exportMessage || '')
                }
                className={classes.messageAction}
                disabled={isExportingToZoriReport}
              />
            )}
            {isPostCopyable && (
              <IconButton
                title={formatMessage(messages.copyMarkdown)}
                aria-label={formatMessage(messages.copyMarkdown)}
                icon={Code}
                onClick={handleCopyMarkdown}
                className={classes.messageAction}
              />
            )}
          </div>
        </Message.Header>
      )}

      <Message.CustomContent>
        {metadataType === 'llm-thinking' ? (
          <LLMThinkingMessage>{messageContent}</LLMThinkingMessage>
        ) : (
          messageContent
        )}
      </Message.CustomContent>
      {isAgentMessage && <Avatar name={maybeAuthor} src={AgentIcon} />}
      <Message.Footer sentTime={displayTime}>
        {/*
          ChatScope's Footer renders `children` whenever they are defined and
          otherwise falls back to `sentTime`, so we only override children for
          feedback messages and re-render the timestamp ourselves alongside the
          thumbs controls.
        */}
        {showBottomFeedback ? (
          <div className={classes.bottomFeedbackFooter}>
            <span className="cs-message__sent-time">{displayTime}</span>
            <div className={classes.bottomFeedbackContainer}>
              <IconButton
                size={0}
                title={formatMessage(messages.thumbsUpResponse)}
                aria-label={formatMessage(messages.thumbsUpResponse)}
                icon={ThumbsUp}
                onClick={onThumbsUp}
                className={classes.bottomFeedbackButton}
              />
              <IconButton
                size={0}
                title={formatMessage(messages.thumbsDownResponse)}
                aria-label={formatMessage(messages.thumbsDownResponse)}
                icon={ThumbsDown}
                onClick={onThumbsDown}
                className={classes.bottomFeedbackButton}
              />
            </div>
          </div>
        ) : undefined}
      </Message.Footer>
    </Message>
  );
};

const LLMThinkingMessage = ({ children }: { children: React.ReactNode }) => {
  const [isOpen, setIsOpen] = useState(false);

  return (
    <ExpansionPanel
      isOpen={isOpen}
      onToggle={setIsOpen}
      title="Thoughts"
      style={{ borderBottom: 'none' }}
    >
      {isOpen ? children : null}
    </ExpansionPanel>
  );
};
